import { ContentfulPreviewEntry } from '../services/contentfulPreviewService'
import { isContentfulAssetFile, isContentfulLink, isRecord } from './overlayContentfulFields/shared'

export type LiveAssetReference = {
  id: string
  /** The file URL, when the live payload carried the asset inline rather than as a bare Link. */
  url?: string
}

/**
 * The asset id of any of the three shapes live preview uses for an asset field.
 *
 * Matching only `sys.type === 'Link'` is not enough and was why image swaps did nothing: the SDK
 * frequently merges the asset in **resolved** (`sys.type === 'Asset'`), so the id of the asset the
 * editor just picked was never collected, never fetched, and never available to the overlay.
 */
const assetReferenceId = (value: unknown): string | undefined => {
  if (isContentfulLink(value, 'Asset')) return value.sys.id

  if (isRecord(value) && isRecord(value.sys) && value.sys.type === 'Asset' && typeof value.sys.id === 'string') {
    return value.sys.id
  }

  return undefined
}

const inlineFileUrl = (value: unknown): string | undefined => {
  if (!isRecord(value)) return undefined

  if (isRecord(value.fields) && isContentfulAssetFile(value.fields.file)) return value.fields.file.url
  if (isContentfulAssetFile(value.file)) return value.file.url

  return undefined
}

/**
 * Every asset the given Preview API entries reference, at any depth, with the inline file URL when
 * the live payload included one.
 *
 * The page include graph is fetched once, so an asset an editor links afterwards — or re-uploads a
 * file to — is either unknown or stale on our side. The URL is what makes a stale one detectable:
 * replacing an asset's file keeps its id and changes only the URL token.
 */
export const collectLiveAssetReferences = (entries: ContentfulPreviewEntry[]): LiveAssetReference[] => {
  const byId = new Map<string, LiveAssetReference>()

  const walk = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(walk)
      return
    }
    if (!isRecord(value)) return

    const id = assetReferenceId(value)
    if (id) {
      const url = inlineFileUrl(value)
      const existing = byId.get(id)
      // A later occurrence carrying the file wins over an earlier bare Link for the same asset.
      if (!existing || (url && !existing.url)) byId.set(id, url ? { id, url } : { id })
      return
    }

    Object.values(value).forEach(walk)
  }

  entries.forEach(entry => walk(entry.fields))

  return Array.from(byId.values())
}

/** Ids only, for callers that do not care whether the live payload carried the file. */
export const collectAssetLinkIds = (entries: ContentfulPreviewEntry[]): string[] =>
  collectLiveAssetReferences(entries).map(reference => reference.id)

const RICH_TEXT_ENTRY_EMBED_NODE_TYPES = new Set(['embedded-entry-block', 'embedded-entry-inline', 'entry-hyperlink'])

/**
 * Entry ids embedded in any rich-text field of the given entries.
 *
 * These need fetching for the same reason assets do: the page include graph was fetched once, so an
 * entry embedded afterwards is unknown. Unlike an asset, an embedded entry also has its **own**
 * links (a OneBlock points at a Copy, which points at an Image, which points at an Asset), so
 * fetching it means fetching its subtree — which `GET /__contentful-preview/:entryId` already does.
 */
export const collectRichTextEmbedEntryIds = (entries: ContentfulPreviewEntry[]): string[] => {
  const ids = new Set<string>()

  const walk = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(walk)
      return
    }
    if (!isRecord(value)) return

    if (typeof value.nodeType === 'string' && RICH_TEXT_ENTRY_EMBED_NODE_TYPES.has(value.nodeType)) {
      const target = isRecord(value.data) ? value.data.target : undefined
      if (isRecord(target) && isRecord(target.sys) && typeof target.sys.id === 'string') {
        ids.add(target.sys.id)
      }
    }

    Object.values(value).forEach(walk)
  }

  entries.forEach(entry => walk(entry.fields))

  return Array.from(ids)
}
